// Modules
import React, { useCallback, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { ApolloError } from '@apollo/client'
import { formatCPF } from '@brazilian-utils/brazilian-utils'

// Application
import { tracking } from 'constants/tracking'
import { ExternalData, ParamsProp } from './types'
import { getQueryParamsValue, singleSPA } from 'utils'
import { useAuthenticatePartnerService } from 'services/authenticatePartner'
import { useAccountService } from 'services/account'
import { useLoginHook } from 'hooks/useLoginHook'
import { ROUTES_PATH } from 'constants/routesPath'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { goToPage } from 'redesign/utils/goToPage'

const ExternalCompany = () => {
    const { company } = useParams<ParamsProp>()
    const location = useLocation()
    const { authenticatExtenalCompany } = useAuthenticatePartnerService()
    const { verifyUser } = useLoginHook()
    const { checkAccountService } = useExtenalCompanyHook()
    const { getConfigByName } = useRemoteConfig()

    const getUserState = useCallback(async () => {
        try {
            const { userType } = await verifyUser({
                externalStep: 'LOGIN',
                loginType: tracking.LOGIN_TYPE.AuthenticatePartner,
            })

            return userType
        } catch (error) {
            singleSPA.historyPush('/erro-de-conexao', { pageContent: 'pageConnectionFailure' })
            throw new Error('error on verifyUser')
        }
    }, [verifyUser])

    const checkAccount = useCallback(
        async federalTaxId => {
            try {
                await checkAccountService(federalTaxId)
            } catch (error) {
                const err: ApolloError = error

                if (err.graphQLErrors?.length) {
                    const codeError = err.graphQLErrors[0].extensions?.code

                    if (codeError === 'UNDERAGE') {
                        return
                    }
                }
                return goToPage('/login')
            }
        },
        [checkAccountService],
    )

    const authenticate = useCallback(
        async ({ token, federalTaxId, app }) => {
            try {
                await checkAccount(federalTaxId)
                const { access_token: accessToken, refresh_token: refreshToken } = await authenticatExtenalCompany(federalTaxId, token)

                const storageData = {
                    token: accessToken,
                    refreshToken: refreshToken,
                    signed: !!accessToken,
                }

                const external: ExternalData = {
                    app,
                    companyName: company,
                }

                sessionStorage.setItem('EXTERNAL_COMPANY_DATA', JSON.stringify(external))
                sessionStorage.setItem('@Livia/federalTaxId', formatCPF(federalTaxId))
                localStorage.setItem('@Livia', JSON.stringify(storageData))

                const userType = await getUserState()
                if (userType === 'FULL') {
                    return goToPage(getConfigByName('AUTH_REDIRECT_LOGIN') || defaultConfig.strings.AUTH_REDIRECT_LOGIN)
                }
                return goToPage(getConfigByName('AUTH_REDIRECT_LOGIN_LIMITED_FLOW') || defaultConfig.strings.AUTH_REDIRECT_LOGIN)
            } catch (error) {
                const err: ApolloError = error

                if (err.graphQLErrors?.length) {
                    const codeError = err.graphQLErrors[0].extensions?.code
                    if (codeError === 'PARTNER_UNAUTHENTICATED') {
                        return singleSPA.historyPush(ROUTES_PATH.NEW_SESSION_EXPIRED)
                    }

                    if (codeError === 'NOTFOUND') {
                        return goToPage(ROUTES_PATH.SIGN_UP_HOME_REDIRECT)
                    }
                }
                return goToPage(ROUTES_PATH.LOGIN)
            }
        },
        [authenticatExtenalCompany, checkAccount, company, getConfigByName, getUserState],
    )

    useEffect(() => {
        localStorage.removeItem('@Livia')
        sessionStorage.removeItem('@Livia/federalTaxId')

        const validateToken = async () => {
            const token = getQueryParamsValue(location, 'token')
            const federalTaxId = getQueryParamsValue(location, 'cpf')
            const app = getQueryParamsValue(location, 'app')
            if (!token || !federalTaxId) {
                return singleSPA.historyPush('/nao-autorizado')
            }

            await authenticate({ token, federalTaxId, app })
            return
        }

        validateToken()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <></>
}

export const useExtenalCompanyHook = () => {
    const { checkAccountService } = useAccountService()
    return { checkAccountService }
}

export default ExternalCompany
